/**
 * Phase 6 — Registration + Waiver + Payment services.
 *
 * Cloudflare Stripe endpoints handle payment checkout and webhook mirroring.
 */

import type {
  Id,
  Registration,
  RegistrationKind,
  RegistrationStatus,
  Waiver,
  WaiverDocument,
  WaiverDocumentKind,
} from '../models';
import { getDb, updateDb } from './store';
import { newId, nowIso } from './ids';

// ---------- Registrations ----------

export function listRegistrations(tournamentId: Id): Registration[] {
  return getDb()
    .registrations.filter((r) => r.tournamentId === tournamentId)
    .sort((a, b) => (b.submittedAt ?? b.createdAt).localeCompare(a.submittedAt ?? a.createdAt));
}

export function getRegistration(id: Id): Registration | undefined {
  return getDb().registrations.find((r) => r.id === id);
}

export interface CreateRegistrationInput {
  tournamentId: Id;
  type: RegistrationKind;
  profileId?: Id;
  teamId?: Id;
  formData?: Record<string, string | number | boolean | null>;
}

export function createRegistration(input: CreateRegistrationInput): Registration {
  const ts = nowIso();
  const reg: Registration = {
    id: newId('reg'),
    kind: 'registration',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    type: input.type,
    profileId: input.profileId,
    teamId: input.teamId,
    status: 'started',
    formData: input.formData ?? {},
  };
  updateDb((db) => ({ ...db, registrations: [...db.registrations, reg] }));
  return reg;
}

export function updateRegistration(id: Id, patch: Partial<Omit<Registration, 'id' | 'kind' | 'createdAt'>>): Registration | undefined {
  let updated: Registration | undefined;
  updateDb((db) => ({
    ...db,
    registrations: db.registrations.map((r) => {
      if (r.id !== id) return r;
      updated = { ...r, ...patch, updatedAt: nowIso() };
      return updated;
    }),
  }));
  return updated;
}

export function setRegistrationStatus(id: Id, status: RegistrationStatus, reviewedBy?: Id): Registration | undefined {
  return updateRegistration(id, {
    status,
    reviewedAt: nowIso(),
    reviewedBy,
  });
}

// ---------- Waivers ----------

export function listWaiverDocuments(tournamentId?: Id): WaiverDocument[] {
  const all = getDb().waiverDocuments;
  if (!tournamentId) return all.filter((w) => w.isTemplate);
  return all.filter((w) => w.tournamentId === tournamentId || w.isTemplate);
}

export function getWaiverDocument(id: Id): WaiverDocument | undefined {
  return getDb().waiverDocuments.find((w) => w.id === id);
}

export function createWaiverDocument(input: {
  tournamentId?: Id;
  isTemplate?: boolean;
  type: WaiverDocumentKind;
  title: string;
  body: string;
  version?: string;
}): WaiverDocument {
  const ts = nowIso();
  const doc: WaiverDocument = {
    id: newId('waiver-doc'),
    kind: 'waiver-document',
    createdAt: ts,
    updatedAt: ts,
    tournamentId: input.tournamentId,
    isTemplate: input.isTemplate ?? !input.tournamentId,
    type: input.type,
    title: input.title,
    body: input.body,
    version: input.version ?? '1.0',
  };
  updateDb((db) => ({ ...db, waiverDocuments: [...db.waiverDocuments, doc] }));
  return doc;
}

export interface SignWaiverInput {
  documentId: Id;
  signerName: string;
  signerEmail?: string;
  profileId?: Id;
  signatureText: string;
  isGuardianSignature?: boolean;
  guardianRelationship?: string;
}

/**
 * Capture a waiver signature. Records IP/user-agent when available
 * (callers in browser context can pass `navigator.userAgent`).
 */
export function signWaiver(input: SignWaiverInput, audit?: { ipAddress?: string; userAgent?: string }): Waiver {
  const ts = nowIso();
  const w: Waiver = {
    id: newId('waiver'),
    kind: 'waiver',
    createdAt: ts,
    updatedAt: ts,
    documentId: input.documentId,
    signerName: input.signerName.trim(),
    signerEmail: input.signerEmail?.trim() || undefined,
    profileId: input.profileId,
    signatureText: input.signatureText.trim(),
    signedAt: ts,
    ipAddress: audit?.ipAddress,
    userAgent: audit?.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined),
    isGuardianSignature: input.isGuardianSignature,
    guardianRelationship: input.guardianRelationship,
  };
  updateDb((db) => ({ ...db, waivers: [...db.waivers, w] }));
  return w;
}
